import { FunctionComponentElement, ReactNode } from "react";
import { type CSSObject } from "@emotion/react";
import { Badge, Colors, Icon, type BadgeProps } from "main";

type IconBlockProps = {
  /**
   * The main content of the block. Can be text, a component, or any valid React node.
   * @required
   */
  content: ReactNode;
  /**
   * Whether to display a divider between the icon and the content.
   * @default false
   */
  hasDivider?: boolean;
  /**
   * Optional badge to display inside the block.
   * If provided, both `status` and `text` are required.
   *
   * @default undefined
   */
  badge?: BadgeProps;
  /**
   * Optional icon displayed on the left side of the content.
   * Intended for visual context or decoration only (non-interactive).
   *
   * @default undefined
   */
  leftIcon?: FunctionComponentElement<typeof Icon>;
  /**
   * Optional icon displayed on the right side of the content.
   * Can be clickable if an `onClick` handler is provided.
   *
   * @default undefined
   */
  rightIcon?: {
    icon: FunctionComponentElement<typeof Icon>;
    onClick: () => void;
  };
};

/**
 * Component | IconBlock
 *
 * Renders a content block with optional left and right icons, an optional status badge,
 * and an optional divider.
 *
 * - `leftIcon` is decorative and non-clickable
 * - `rightIcon` is optional and can be made clickable by providing an `onClick` handler
 * - `badge` shows a status indicator with text when provided
 * - `hasDivider` controls whether a bottom border is rendered
 *
 * @Figma https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=15665-84568&t=hFVjfTgq5PUFnOoV-4
 */

export function IconBlock(props: IconBlockProps) {
  const { content, hasDivider, badge, leftIcon, rightIcon } = props;
  const layoutStyle = generateLayoutStyle({
    hasLeftIcon: !!leftIcon,
    hasRightIcon: !!rightIcon?.icon,
  });

  return (
    <>
      <div css={[styles.row, layoutStyle]}>
        {/* Left Icon */}
        {leftIcon && leftIcon}

        <div css={styles.contentRow}>
          <div>{content}</div>

          {/* Badge */}
          {badge && (
            <div css={{ flexShrink: 0 }}>
              <Badge status={badge.status} text={badge.text} />
            </div>
          )}
        </div>

        {/* Right Icon */}
        {rightIcon?.icon && (
          <span css={{ cursor: "pointer" }} onClick={rightIcon.onClick}>
            {rightIcon.icon}
          </span>
        )}
      </div>

      {/* Divider */}
      {hasDivider && <div css={styles.divider} />}
    </>
  );
}

/********** Helper Function **********/

/**
 * Determines the grid layout style for the IconBlock based on the presence
 * of left and right icons. This controls how the content and icons are arranged.
 *
 * Using an object for clarity.
 */

type GenerateLayoutStyleParams = {
  hasLeftIcon: boolean;
  hasRightIcon: boolean;
};

export function generateLayoutStyle(params: GenerateLayoutStyleParams) {
  const { hasLeftIcon, hasRightIcon } = params;
  if (hasLeftIcon && hasRightIcon) return styles.iconBoth;
  if (hasLeftIcon) return styles.iconLeft;
  if (hasRightIcon) return styles.iconRight;
  return styles.iconNone;
}

/********** Styles **********/
const styles = {
  row: {
    display: "grid",
    gap: "0 16px",
    padding: "14px 16px",
  },
  iconNone: { gridTemplateColumns: "1fr" },
  iconLeft: { gridTemplateColumns: "24px 1fr" },
  iconRight: { gridTemplateColumns: "1fr 24px" },
  iconBoth: { gridTemplateColumns: "24px 1fr 24px" },
  divider: {
    width: "100%",
    height: 1,
    backgroundColor: Colors.slate200,
  },
  contentRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
} satisfies Record<string, CSSObject>;
